import { AppShell } from '@bbl-digital/snorre'
import {
  BBLEnum,
  Feedback,
  Select,
  ThemeShell,
  useShowFeedbackOnRouteChange,
} from '@bbl-digital/snorre-v2'
import AfterLogout from 'Auth/AfterLogout'
import Cleanup from 'Auth/Cleanup'
import Logout from 'Auth/Logout'
import { useAuth } from 'Auth/shared/hooks/useAuth'
import { hasCodeInUrl } from 'Auth/shared/utils/codeInUrl'
import { setBearerToken } from 'axiosConfig'
import ErrorBoundary from 'ErrorBoundary'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { Route, Switch, useLocation } from 'react-router-dom'
import NotAuthorizedPage from 'shared/components/NotAuthorizedPage'
import PageError from 'shared/components/PageError'
import PageLoader from 'shared/components/PageLoader'
import useAppRedirectHook from 'shared/hooks/useAppRedirectHook'
import { useHandleFeedback } from 'shared/hooks/useHandleFeedback'
import useInitEmailSettings from 'shared/hooks/useInitializeEmailSettings'
import useSignalR from 'shared/hooks/useSignalR'
import { getBblDisplayNames } from 'shared/store/bbl'
import { useLazyGetBblQuery } from 'shared/store/bbl/bbl.service'
import {
  clientApiSelector,
  getActiveClientId,
} from 'shared/store/clients/selectors'
import { useLazyGetCustomTextsQuery } from 'shared/store/customtexts/customtexts.service'
import { useLazyGetUserQuery } from 'shared/store/user/user.service'
import { IBaseQueryError } from 'shared/utils/axiosBaseQuery'
import { isTestEnvironment } from 'shared/utils/isTestEnvironment'
import { isUsbl, setFavicon } from 'shared/utils/theme'
import AnalyticsHandler from './AnalyticsHandler'
import BrowserSupport from './BrowserSupport'
import GlobalSearchHandler from './GlobalSearchHandler'
import RelationPicker from './RelationPicker'
import Routes from './Routes'

const AppRoutes: React.FC = () => (
  <Switch>
    <Route path="/logout" exact component={Logout} />
    <Route path="/afterlogout" exact component={AfterLogout} />
    <Route path="/cleanup" component={Cleanup} />
    <Route path="/" component={App} />
  </Switch>
)

const App: React.FC = () => {
  const [getCustomTexts] = useLazyGetCustomTextsQuery()
  const { portalShort, portal } = useSelector(getBblDisplayNames)
  const [getUser, { error: userBaseError }] = useLazyGetUserQuery()
  const [getBbl, { data, isLoading, error }] = useLazyGetBblQuery()
  const { onPostFeedback, isPostingFeedback, isFeedbackError } =
    useHandleFeedback()
  const location = useLocation()

  const userError = userBaseError as IBaseQueryError['error']

  const currentClientId = useSelector(getActiveClientId)
  const { isError, isLoading: isClientLoading } = useSelector(
    clientApiSelector(currentClientId)
  )

  const [themeUrl, setThemeUrl] = useState<string>()
  const [brandingCode, setBrandingCode] = useState<string>()
  const authContext = useAuth()
  const { initSignalR } = useSignalR()

  const appDisplayName = isUsbl() ? portal : portalShort

  // Hook for redirect for bate
  useAppRedirectHook()

  // Initializes email config & signature
  useInitEmailSettings()

  useShowFeedbackOnRouteChange({
    pathname: location.pathname,
    skip: isTestEnvironment,
  })

  const fatalError = Boolean(
    error || isError || (userError && userError?.status !== 404)
  )

  useEffect(() => {
    getBblAndUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getBblAndUser = async () => {
    /**
     * If url is oidc callback, return
     */
    if (hasCodeInUrl(window.location)) return

    if (authContext.userData?.access_token) {
      setBearerToken(authContext.userData.access_token)
    }

    const bbl = await getBbl().unwrap()
    // Require to fetch theme first to have correct buttons if an error occurs.
    handleTheme(bbl.brandingCode)

    getUser()
    getCustomTexts()
    initSignalR()
  }

  const handleTheme = (brandingCode: string) => {
    const code = brandingCode.toLowerCase()
    window.brandingCode = code
    setThemeUrl(window.config.urls.themesUrl + code)
    setFavicon(code)
    setBrandingCode(code)
  }

  return (
    <ErrorBoundary>
      {/** Global styles */}

      {/** Loading state */}
      {(isLoading || isClientLoading) && <PageLoader />}

      {data && (
        <Helmet>
          <title>{data.settings?.displayNames?.portalDisplayNameShort}</title>
        </Helmet>
      )}

      {/** Application loaded */}
      {themeUrl && brandingCode && data && !fatalError && (
        <AppShell
          themeUrl={themeUrl}
          globalsFolder={isUsbl() ? 'portal' : undefined}
        >
          <ThemeShell bbl={brandingCode as BBLEnum} variant="portal">
            <AnalyticsHandler bbl={data}>
              <BrowserSupport>
                {currentClientId && <GlobalSearchHandler />}
                <Feedback
                  ingress={`Hva synes du om ${appDisplayName}?`}
                  onSend={(feedback) => onPostFeedback(feedback)}
                  loading={isPostingFeedback}
                  error={
                    isFeedbackError
                      ? 'Det oppsto en feil under sending av tilbakemelding. Prøv igjen senere'
                      : undefined
                  }
                  bannerText={`Husk at du alltid kan gi tilbakemelding via avataren din i topplinjen til høyre. Tilbakemeldingene er anonyme og blir ikke besvart – de brukes kun til å forbedre ${portal}.`}
                />
                {userError?.status !== 404 ? (
                  <>
                    <RelationPicker>
                      <Routes />
                    </RelationPicker>
                  </>
                ) : (
                  // We need to be able to log out if we are not authorized
                  <NotAuthorizedPage authenticationError showLogOut />
                )}
                {isTestEnvironment && (
                  <div className="absolute top-0 right-0 z-10 opacity-0 hover:opacity-100">
                    <Select
                      onValueChange={(value) => {
                        handleTheme(value)
                      }}
                      clearable={false}
                    >
                      <Select.Content>
                        {Object.keys(BBLEnum)
                          .sort((a, b) => a.localeCompare(b))
                          .map((key) => (
                            <Select.Item key={key} value={key}>
                              {key}
                            </Select.Item>
                          ))}
                      </Select.Content>
                    </Select>
                  </div>
                )}
              </BrowserSupport>
            </AnalyticsHandler>
          </ThemeShell>
        </AppShell>
      )}

      {/** Error page */}
      {fatalError && <PageError />}
    </ErrorBoundary>
  )
}

export default AppRoutes
